// @ts-ignore
import {JSX, Fragment, h, forceUpdate} from '@stencil/core';
import { injectHistory, type RouterHistory } from "@stencil-community/router";

interface RouterRoutesObject {
  ROOT: string,
  [key: string]: string
}

interface RouterRenderStackObject {
  [key: string]: string
}

interface RouterRoutable {
  STENCIL_ROUTER_LAST_URL?: string;
  STENCIL_ROUTER_ROOT?: string;
  STENCIL_ROUTER_ROUTES?: RouterRoutesObject;
  STENCIL_ROUTER_RENDER_STACKS?: RouterRenderStackObject;
  STENCIL_ROUTER_IS_ACTIVE: boolean;

  setRouterIsActive(state: boolean): void;
  getRouterIsActive(): boolean;
  activeOnRoutes(): boolean;
  getRouterRoot(): void;
  getRouterRoutes(): RouterRoutesObject;
  getRouterRoute(route:string, placeholder?: any | null): string;
  getRouterRouteHref(route:string, placeholder?: any | null): string;
  pushRouterRoute(route: string): void;
  reloadRouterRoute(route: string): void;
  renderRouterRoute(routeName: string, route: any, context: any, props: any): JSX.Element;
  StencilRouterOutput(context?: any): JSX.Element;
}

/**
 * Router
 * @constructor
 */
// @ts-ignore
export const Router = (routes: any, group: string=null, historyType: HistoryType="hash") => {
  //@ts-ignore
  return (proto, _method) => {
    const { componentWillLoad, render } = proto;

    /**
     * componentWillLoad
     */
    proto.componentWillLoad = function() {
      /**
       * setRouterIsActive
       */
      proto.setRouterIsActive = (state: boolean): void => {
        this.STENCIL_ROUTER_IS_ACTIVE = state;

        if (!state) {
          this.cleanRouterRoute();
        }
      };

      /**
       * getRouterIsActive
       */
      proto.getRouterIsActive = (): boolean => {
        return this.STENCIL_ROUTER_IS_ACTIVE;
      };

      /**
       * getRouterRoot
       */
      proto.getRouterRoot = (): string => {
        this.STENCIL_ROUTER_ROOT = `/${this.element.localName}`;
        return this.STENCIL_ROUTER_ROOT;
      };

      /**
       * getRouterRoutes
       */
      proto.getRouterRoutes = (): RouterRoutesObject => {
        let root = this.getRouterRoot();

        this.STENCIL_ROUTER_ROUTES = {
          ROOT: root
        };

        Object.keys(routes).map((routeName) => {
          let route = routes[routeName];
          this.STENCIL_ROUTER_ROUTES[routeName] = root + route.pattern;
        });

        return this.STENCIL_ROUTER_ROUTES;
      }

      /**
       * getRouterRoute
       * @param route
       * @param placeholder
       */
      proto.getRouterRoute = (route:string, placeholder?: any | null): string => {
        let routes: any = this.getRouterRoutes(),
            _route: string = routes[route];

        if (!_route) {
          console.error("getRouterRoute: route not found by " + route, placeholder);
        }

        if (placeholder) {
          Object.keys(placeholder).forEach((key) => {
            _route = _route.replace(key, placeholder[key]);
          });
        }

        return _route;
      };

      /**
       * getRouterRouteHref
       * @param route
       * @param placeholder
       */
      proto.getRouterRouteHref = (route: string, placeholder?: any | null): string => {
        return '#' + this.getRouterRoute(route, placeholder);
      };

      /**
       * pushRouterRoute
       * @param route
       */
      proto.pushRouterRoute = (route: string): void => {
        if (this.STENCIL_ROUTER_IS_ACTIVE && this.history) this.history.push(route);
      };

      /**
       * reloadRouterRoute
       * @param route
       */
      proto.reloadRouterRoute = (route: string): void => {
        this.STENCIL_ROUTER_LAST_URL = "force";
        if (this.STENCIL_ROUTER_IS_ACTIVE && this.history) this.history.push(route);
        forceUpdate(this);
      };

      /**
       * cleanRouterRoute
       */
      proto.cleanRouterRoute = (): void => {
        if (!this.history) return;

        let uri = window.location.toString(),
            cleanUri = uri.substring(0, uri.indexOf("#"));

        window.history.replaceState({}, document.title, cleanUri);
      }

      /**
       * activeOnRoutes
       */
      proto.activeOnRoutes = (routes: any) :boolean => {
        if (this.history && routes.filter(route => {
          return this.getRouterRoute(route) === this.history.location.pathname
        }).length > 0) {
          return true;
        }

        return false;
      };

      /**
       * renderRouterRoute
       * @param routeName
       * @param route
       * @param context
       * @param props
       */
      proto.renderRouterRoute = (routeName: string, route: any, context: any, props: any): JSX.Element => {
        Object.keys(this.STENCIL_ROUTER_RENDER_STACKS['all']).map((method: string) => {
          let clazz = this.STENCIL_ROUTER_RENDER_STACKS['all'][method];
          clazz[method].call(this, {...context, ...props});
        });

        if (this.STENCIL_ROUTER_RENDER_STACKS[routeName]) {
          Object.keys(this.STENCIL_ROUTER_RENDER_STACKS[routeName]).map((method: string) => {
            if (
              (this.STENCIL_ROUTER_LAST_URL !== props.match.url && props.match.url === this.history.location.pathname) ||
              this.STENCIL_ROUTER_LAST_URL === "force"
            ) {
              if (this.STENCIL_ROUTER_LAST_URL === "force") {
                this.STENCIL_ROUTER_LAST_URL = undefined;
              }

              let clazz = this.STENCIL_ROUTER_RENDER_STACKS[routeName][method];
              clazz[method].call(this,{...context, ...props});
            }
          });
        }

        this.STENCIL_ROUTER_LAST_URL = props.match.url;

        return route.render.call(this, {...context, ...props});
      };

      return componentWillLoad && componentWillLoad.call(this);
    };

    /**
     * render
     */
    proto.render = function(): JSX.Element {
      if (this.STENCIL_ROUTER_IS_ACTIVE === undefined) this.STENCIL_ROUTER_IS_ACTIVE = true;

      //@ts-ignore
      this.StencilRouterOutput = (context?: any): JSX.Element => {
        let routerRoutes = this.getRouterRoutes(),
            notFound = routerRoutes['NOT_FOUND'] || null;

        if (notFound) {
          delete routerRoutes['NOT_FOUND'];
        }

        //@ts-ignore
        return this.STENCIL_ROUTER_IS_ACTIVE ? <Fragment>
          { /* @ts-ignore */ }
          <stencil-router historyType={historyType} />
          { /* @ts-ignore */ }
          <stencil-route-switch group={group || this.getRouterRoot()}>
            { Object.keys(routes).map((routeName) => {
              let route = routes[routeName];

              return (
                  /* @ts-ignore */
                  <stencil-route
                      url={routerRoutes[routeName] || ""}
                      group={group || this.getRouterRoot()}
                      componentProps={ typeof route.properties === "function" ? route.properties.call(this, context) : {} }
                      routeRender={(props: { [key: string]: any }) => this.renderRouterRoute(routeName, route, context, props)}
                      exact={route.exact || false}
                  >
                  { /* @ts-ignore not found */}
                  </stencil-route>
              );
            }) }

            { /* @ts-ignore not found */}
            <stencil-route
                group={group || this.getRouterRoot()}
                componentProps={ (notFound && typeof notFound.properties === "function") ? notFound.properties.call(this, context) : {} }
                routeRender={(props: { [key: string]: any }) => notFound ? this.renderRouterRoute('NOT_FOUND', notFound, context, props) : this.renderRouterRoute('NOT_FOUND', {
                  //@ts-ignore
                  render: () => <Fragment>Seite nicht gefunden!</Fragment>
                }, context, props)}
            >
            { /* @ts-ignore not found */}
            </stencil-route>
          { /* @ts-ignore not found */}
          </stencil-route-switch>
        </Fragment> : ''
      };

      return render && render.call(this);
    };

    /**
     * inject router history to use it in this webcomponent
     */
    injectHistory(proto.constructor);
  };
}

/**
 * RouterRouteRender
 * @param specificRoute
 */
export const RouterRouteRender = (specificRoute?: string) => {
  return (proto: any, method: any) => {
    const {connectedCallback} = proto;

    proto.connectedCallback = function(): void {
      let callRoute = 'all';
      if (specificRoute) {
        callRoute = specificRoute;
      }

      if (!proto.STENCIL_ROUTER_LAST_URL) {
        proto.STENCIL_ROUTER_LAST_URL = undefined;
      }

      if (!proto.STENCIL_ROUTER_RENDER_STACKS) {
        proto.STENCIL_ROUTER_RENDER_STACKS = {};
      }

      if (!proto.STENCIL_ROUTER_RENDER_STACKS[callRoute]) {
        proto.STENCIL_ROUTER_RENDER_STACKS[callRoute] = {};
      }

      proto.STENCIL_ROUTER_RENDER_STACKS[callRoute][method] = this;

      return connectedCallback && connectedCallback.call(this);
    };
  };
};

export { RouterHistory, RouterRoutable };
